import request from 'umi-request'
import Cookies from 'js-cookie'
import { store } from '../../../store/'

async function getAuthService() {
    const m = await import('../ServiceAuth')
    return m.authService
}

/** One in-flight refresh for concurrent 401s (avoids duplicate refresh + cookie races). */
let refreshJwtPromise = null

function getLanguageFromRedux() {
    const newState = store.getState()

    return newState.language
}

store.subscribe(getLanguageFromRedux)

export function queryParams(location) {
    return new URLSearchParams(location?.search)
}

export async function handlePDFGeneration(method, endpoint) {
    const authService = await getAuthService()
    const accessToken = await authService.getAccessToken()
    if (!accessToken) return
    const language = getLanguageFromRedux()
    const lang = language === 'cz' ? 'cs' : language
    const xhr = new XMLHttpRequest()
    xhr.open(method, endpoint, true)
    xhr.responseType = 'blob'
    xhr.setRequestHeader('Content-type', 'application/json;charset=UTF-8')
    xhr.setRequestHeader('Authorization', 'Bearer ' + accessToken)
    xhr.setRequestHeader('Accept-Language', lang || 'en-us')
    xhr.onload = function () {
        if (this.status === 200) {
            const blob = new Blob([this.response], { type: 'application/pdf' })
            const fileURL = URL.createObjectURL(blob)
            window.open(fileURL, '_blank')
        }
    }
    xhr.send()
}

export async function fileDownload(method, endpoint, type, filename, body, download = false) {
    const authService = await getAuthService()
    const accessToken = await authService.getAccessToken()
    if (!accessToken) return Promise.reject({ message: 'Not authenticated.' })
    const language = getLanguageFromRedux()
    const lang = language === 'cz' ? 'cs' : language
    return new Promise(function (resolve, reject) {
        const xhr = new XMLHttpRequest()
        xhr.open(method, endpoint, true)
        xhr.responseType = 'blob'
        xhr.setRequestHeader('Content-type', 'application/json;charset=UTF-8')
        xhr.setRequestHeader('Authorization', 'Bearer ' + accessToken)
        xhr.setRequestHeader('Accept-Language', lang || 'en-us')
        xhr.onload = function () {
            if (this.status === 200) {
                const blob = new Blob([this.response], { type: type })
                const fileURL = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = fileURL
                if (type !== 'application/pdf' || download) {
                    link.setAttribute('download', filename)
                } else {
                    link.setAttribute('target', '_blank')
                }
                document.body.appendChild(link)
                link.click()
                link.parentNode.removeChild(link)
                resolve({ status: 200, response: null })
            } else {
                reject({ message: 'Something went wrong. Please try again later.' })
            }
        }
        if (body === undefined) {
            xhr.send()
        } else {
            xhr.send(JSON.stringify(body))
        }
    })
}

async function APICall(config, accessToken) {
    let language = getLanguageFromRedux()
    language = language === 'cz' ? 'cs' : language

    let headers = {}
    if (config.private) {
        if (config?.authorization) {
            headers = {
                Authorization: config?.authorization,
                'Accept-Language': config.language || language,
            }
        } else {
            headers = {
                Authorization: `Bearer ${accessToken}`,
                'Accept-Language': config.language || language,
            }
        }
    } else {
        headers = {
            'Accept-Language': config.language || language,
        }
    }

    if (config.headers && typeof config.headers === 'object') {
        headers = {
            ...headers,
            ...config.headers,
        }
    }

    return await request(config.url, {
        method: config.method,
        data: config.data,
        getResponse: true,
        headers: headers,
    })
        .then((response) => {
            return {
                status: response.response.status,
                response: response.data,
            }
        })
        .catch((error) => {
            if (error.response) {
                if (error.response.status >= 500) {
                    console.log('Critical error')
                }
                /*if (error.response.status === 403) {
                    permissionErrorAlert()
                }
                if (error.response.status === 401) {
                    unAuthorizedErrorAlert()
                }*/
                const parsed =
                    error.data !== undefined && error.data !== null
                        ? error.data
                        : error.response?.data !== undefined
                          ? error.response.data
                          : null
                return {
                    status: error.response.status,
                    response: parsed,
                }
            } else {
                if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                    console.log('Network error')
                }
                return {
                    status: 500,
                    response: null,
                }
            }
        })
}

/*
    Example USAGE:

    async function getUserProfile(){
        return await APIRequest({
            url: .../login/,  // API.LOGIN
            method: 'POST', // POST, PUT, PATCH, DELETE
            data: {
                "username": "admin",
                "password": "admin"
            },
            private: false // by default APIRequest is Private, if you want to use public use false value
        });
    }

    Response on Success:
    {
        status: 200, // or other success codes
        response: {...} // as data response object that could contain data or other type of information
    }

    Response on Failure:
    {
        status: 400, // or other failure codes
        response: {...} // as data response object, that could contain error messages
    }

*/

/**
 * Single source of truth for authenticated API requests.
 * - Attaches Bearer token from cookies.
 * - On 401: refreshes once via authService.refreshLogin, retries request once, then returns.
 * - On refresh failure or no refresh token: clears cookies (no infinite retry).
 */
export async function APIRequest(config) {
    let accessToken = Cookies.get('accessToken')
    const refreshToken = Cookies.get('refreshToken')
    let result = {}

    if ((accessToken || config?.authorization) && config.private) {
        if (config?.authorization) {
            result = await APICall(config, null)
            if (result?.status === 401) {
                console.log('Unauthorized')
            }
        } else {
            result = await APICall(config, accessToken)
            if (result?.status === 401) {
                if (refreshToken) {
                    if (!refreshJwtPromise) {
                        refreshJwtPromise = (async () => {
                            const authService = await getAuthService()
                            return authService.refreshLogin(refreshToken)
                        })().finally(() => {
                            refreshJwtPromise = null
                        })
                    }
                    const refreshOk = await refreshJwtPromise
                    if (refreshOk) {
                        accessToken = Cookies.get('accessToken')
                        result = await APICall(config, accessToken)
                    }
                } else {
                    Cookies.remove('accessToken')
                    Cookies.remove('refreshToken')
                }
            }
        }
    } else {
        result = await APICall(config, accessToken)
    }

    return result
}



export const requests = { get, post, put, patch, del }

async function get(endpoint, isPrivate, customAuthorization) {
    customAuthorization = customAuthorization || null
    if (isPrivate === undefined) {
        isPrivate = true
    }

    return await APIRequest({
        url: endpoint,
        method: 'GET',
        private: isPrivate,
        authorization: customAuthorization,
    })
}

async function post(endpoint, data, isPrivate, customAuthorization, extraHeaders) {
    customAuthorization = customAuthorization || null
    if (isPrivate === undefined) {
        isPrivate = true
    }

    return await APIRequest({
        url: endpoint,
        method: 'POST',
        data: data,
        private: isPrivate,
        authorization: customAuthorization,
        headers: extraHeaders,
    })
}

async function put(endpoint, data, isPrivate, customAuthorization, extraHeaders) {
    customAuthorization = customAuthorization || null
    if (isPrivate === undefined) {
        isPrivate = true
    }

    return await APIRequest({
        url: endpoint,
        method: 'PUT',
        data: data,
        private: isPrivate,
        authorization: customAuthorization,
        headers: extraHeaders,
    })
}

async function patch(endpoint, data, isPrivate, customAuthorization, extraHeaders) {
    customAuthorization = customAuthorization || null
    if (isPrivate === undefined) {
        isPrivate = true
    }

    return await APIRequest({
        url: endpoint,
        method: 'PATCH',
        data: data,
        private: isPrivate,
        authorization: customAuthorization,
        headers: extraHeaders,
    })
}

async function del(endpoint, isPrivate, customAuthorization, extraHeaders) {
    customAuthorization = customAuthorization || null
    isPrivate = isPrivate || true

    return await APIRequest({
        url: endpoint,
        method: 'DELETE',
        private: isPrivate,
        authorization: customAuthorization,
        headers: extraHeaders,
    })
}
